import React, { useState } from "react";
import { DoorOpen, Clock, LogIn, LogOut, Trash2, Plus, Shield, RefreshCw } from "lucide-react";
import { Button } from "../shared/Button";
import { puntosAcceso as apiPuntos, turnos as apiTurnos, panel, usuarios, fotos, catalogos } from "../../api/modulos.service";
import { estilos as est, Cabecera, Vacio, Aviso, Ventana, Entrada, Seleccion, useCarga } from "./base";

/**
 * PUNTOS DE ACCESO Y TURNOS.
 *
 * Van juntos porque se administran juntos: un turno se asigna a un guardia
 * **y a un punto**, y dar de alta el punto es el paso previo obligado. En
 * pantallas separadas, quien crea el primer turno descubre a mitad de camino
 * que le falta algo.
 */

const hoy = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/El_Salvador" });

export function PuntosAcceso() {
  const [abierta, setAbierta] = useState(false);
  const [form, setForm] = useState({ name: "", kind: "mixto", description: "", isDefault: false });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const { datos, cargando, error: errorCarga, recargar } = useCarga(() => apiPuntos.listar({ todos: "1" }), []);
  const lista = datos?.accessPoints || [];

  const guardar = async (e) => {
    e.preventDefault();
    setError(""); setGuardando(true);
    try {
      await apiPuntos.crear(form);
      setAbierta(false); setForm({ name: "", kind: "mixto", description: "", isDefault: false });
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo crear");
    } finally { setGuardando(false); }
  };

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Puntos de acceso"
        subtitulo="Por dónde se entra y se sale. Cada registro dice por cuál pasó."
        accion="Crear punto"
        alAccionar={() => setAbierta(true)}
        icono={DoorOpen}
      />

      {error && <Aviso>{error}</Aviso>}

      {errorCarga ? (
        <Aviso>
          {errorCarga}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
              Reintentar
            </Button>
          </div>
        </Aviso>
      ) : cargando ? <Vacio>Cargando…</Vacio> : lista.length === 0 ? (
        <Vacio>Todavía no hay puntos de acceso. Cree al menos «Entrada principal».</Vacio>
      ) : (
        <div style={est.rejilla}>
          {lista.map((p) => (
            <div key={p._id} style={{ ...est.tarjeta, opacity: p.isActive ? 1 : 0.6 }}>
              <h3 style={est.tarjetaTitulo}>{p.name}</h3>
              <div style={est.tarjetaDato}>
                {p.kind}{p.isDefault ? " · por defecto" : ""}{p.isActive ? "" : " · desactivado"}
              </div>
              {p.description && <div style={est.tarjetaDato}>{p.description}</div>}
              {p.isActive && (
                <div style={est.tarjetaAcciones}>
                  <Button size="sm" variant="ghost" leftIcon={Trash2}
                    onClick={() => {
                      if (window.confirm(`¿Desactivar «${p.name}»? El historial que pasó por él se conserva.`)) {
                        apiPuntos.desactivar(p._id).then(recargar);
                      }
                    }}>
                    Desactivar
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Ventana titulo="Crear un punto de acceso" abierta={abierta} alCerrar={() => setAbierta(false)}
        pie={<>
          <Button variant="ghost" onClick={() => setAbierta(false)}>Cancelar</Button>
          <Button variant="primary" onClick={guardar} loading={guardando}>Crear</Button>
        </>}>
        {error && <Aviso>{error}</Aviso>}
        <Entrada etiqueta="Nombre" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Entrada principal" required />
        <Seleccion etiqueta="Tipo" value={form.kind}
          onChange={(e) => setForm({ ...form, kind: e.target.value })}
          opciones={[
            { valor: "mixto", etiqueta: "Mixto" },
            { valor: "vehicular", etiqueta: "Vehicular" },
            { valor: "peatonal", etiqueta: "Peatonal" },
          ]} />
        <Entrada etiqueta="Descripción" value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <label style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
          <input type="checkbox" checked={form.isDefault}
            onChange={(e) => setForm({ ...form, isDefault: e.target.checked })} />
          <span style={est.etiqueta}>Proponerlo por defecto en la caseta</span>
        </label>
      </Ventana>
    </div>
  );
}

export function Turnos({ rol }) {
  const [abierta, setAbierta] = useState(false);
  const [form, setForm] = useState({ name: "", startTime: "06:00", endTime: "14:00", guard: "", accessPoint: "", date: hoy(), isTemplate: false });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const { datos, cargando, error: errorCarga, recargar } = useCarga(() => apiTurnos.listar({}), []);
  const puntos = useCarga(() => apiPuntos.listar({}), []);
  const directorio = useCarga(() => (rol === "admin" ? panel.directorio() : Promise.resolve({ guardias: [] })), [rol]);
  // Los dos alimentan el formulario de turnos: si fallan, el selector sale
  // vacío y parece que no hay guardias ni puntos creados.
  const errorApoyo = puntos.error || directorio.error;

  const lista = datos?.shifts || [];
  const esAdmin = rol === "admin";

  const guardar = async (e) => {
    e.preventDefault();
    setError(""); setGuardando(true);
    try {
      await apiTurnos.crear({ ...form, guard: form.isTemplate ? undefined : form.guard });
      setAbierta(false); recargar();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || "No se pudo crear");
    } finally { setGuardando(false); }
  };

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Turnos"
        subtitulo={esAdmin ? "Quién cubre qué y dónde." : "Sus turnos. Marque cuando entre y cuando salga."}
        accion={esAdmin ? "Asignar turno" : null}
        alAccionar={() => setAbierta(true)}
        icono={Clock}
      />

      {(error || errorApoyo) && <Aviso>{error || errorApoyo}</Aviso>}

      {errorCarga ? (
        <Aviso>
          {errorCarga}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
              Reintentar
            </Button>
          </div>
        </Aviso>
      ) : cargando ? <Vacio>Cargando…</Vacio> : lista.length === 0 ? (
        <Vacio>No hay turnos.</Vacio>
      ) : (
        <div style={est.rejilla}>
          {lista.map((t) => (
            <div key={t._id} style={est.tarjeta}>
              <h3 style={est.tarjetaTitulo}>{t.name}</h3>
              <div style={est.tarjetaDato}>{t.startTime}–{t.endTime}</div>
              {t.guard?.name && <div style={est.tarjetaDato}>{t.guard.name}</div>}
              {t.accessPoint?.name && <div style={est.tarjetaDato}>{t.accessPoint.name}</div>}
              <div style={{ ...est.tarjetaDato, fontWeight: 600 }}>{t.status.replace("_", " ")}</div>

              {!t.isTemplate && (
                <div style={est.tarjetaAcciones}>
                  {!t.checkInAt && (
                    <Button size="sm" variant="primary" leftIcon={LogIn}
                      onClick={() => apiTurnos.marcarEntrada(t._id).then(recargar).catch((e) => setError(e?.response?.data?.message))}>
                      Marcar entrada
                    </Button>
                  )}
                  {t.checkInAt && !t.checkOutAt && (
                    <Button size="sm" variant="secondary" leftIcon={LogOut}
                      onClick={() => apiTurnos.marcarSalida(t._id).then(recargar).catch((e) => setError(e?.response?.data?.message))}>
                      Marcar salida
                    </Button>
                  )}
                  {esAdmin && !t.checkInAt && (
                    <Button size="sm" variant="ghost" leftIcon={Trash2}
                      onClick={() => apiTurnos.eliminar(t._id).then(recargar).catch((e) => setError(e?.response?.data?.message))}>
                      Eliminar
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Ventana titulo="Asignar un turno" abierta={abierta} alCerrar={() => setAbierta(false)}
        pie={<>
          <Button variant="ghost" onClick={() => setAbierta(false)}>Cancelar</Button>
          <Button variant="primary" onClick={guardar} loading={guardando}>Guardar</Button>
        </>}>
        {error && <Aviso>{error}</Aviso>}
        <Entrada etiqueta="Nombre del turno" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Turno noche" required />
        <div style={est.fila}>
          <Entrada etiqueta="Desde" type="time" value={form.startTime}
            onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
          <Entrada etiqueta="Hasta" type="time" value={form.endTime}
            onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
        </div>
        <label style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
          <input type="checkbox" checked={form.isTemplate}
            onChange={(e) => setForm({ ...form, isTemplate: e.target.checked })} />
          <span style={est.etiqueta}>Es una plantilla (sin fecha ni guardia)</span>
        </label>
        {!form.isTemplate && (
          <>
            <Entrada etiqueta="Fecha" type="date" value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })} />
            <Seleccion etiqueta="Guardia" value={form.guard}
              onChange={(e) => setForm({ ...form, guard: e.target.value })}
              opciones={[{ valor: "", etiqueta: "Elija un guardia" },
                ...(directorio.datos?.guardias || []).map((g) => ({ valor: g._id, etiqueta: g.name }))]} />
            <Seleccion etiqueta="Punto de acceso" value={form.accessPoint}
              onChange={(e) => setForm({ ...form, accessPoint: e.target.value })}
              opciones={[{ valor: "", etiqueta: "Sin asignar" },
                ...(puntos.datos?.accessPoints || []).map((p) => ({ valor: p._id, etiqueta: p.name }))]} />
          </>
        )}
      </Ventana>
    </div>
  );
}

/**
 * FICHA DE LOS GUARDIAS.
 *
 * Quién está de turno, en qué punto, y cómo llamarlo. Hasta ahora un guardia
 * era una fila más en la lista de usuarios, lo que dejaba fuera precisamente
 * lo que hace falta saber de un guardia: dónde está asignado y si está de
 * turno ahora mismo.
 *
 * La fotografía sólo aparece si la residencial la habilitó
 * (`guard_photos_enabled`, apagada de fábrica). Guardar la cara de un
 * empleado no es una decisión que deba tomar el software.
 */
export function Guardias() {
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ phone: "", documentId: "", defaultAccessPoint: "" });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [subiendo, setSubiendo] = useState(false);

  const { datos, cargando, error: errorCarga, recargar } = useCarga(() => usuarios.listar(), []);
  const { datos: puntos } = useCarga(() => apiPuntos.listar({}), []);
  const { datos: cat } = useCarga(() => catalogos(), []);

  const lista = (datos?.users || datos || []).filter((u) => u.role === "guard");
  const fotosDeGuardia = Boolean(cat?.modulos?.fotosGuardia);

  const abrir = (g) => {
    setEditando(g);
    setForm({
      phone: g.phone || "",
      documentId: g.documentId || "",
      defaultAccessPoint: g.defaultAccessPoint?._id || g.defaultAccessPoint || "",
    });
    setError("");
  };

  const guardar = async () => {
    setError("");
    setGuardando(true);
    try {
      await usuarios.fichaGuardia(editando._id, {
        ...form,
        defaultAccessPoint: form.defaultAccessPoint || null,
      });
      setEditando(null);
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo guardar la ficha");
    } finally {
      setGuardando(false);
    }
  };

  const subirFoto = async (archivo) => {
    if (!archivo) return;
    setError("");
    setSubiendo(true);
    try {
      const { key } = await fotos.subir(archivo, { ambito: "guardias", recursoId: editando._id });
      await usuarios.fichaGuardia(editando._id, { photoKey: key });
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo subir la fotografía");
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <div style={est.seccion}>
      <Cabecera titulo="Guardias" subtitulo="Quién está de turno, dónde, y cómo llamarlo." icono={Shield} />

      {error && <Aviso>{error}</Aviso>}

      {errorCarga ? (
        <Aviso>
          {errorCarga}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
              Reintentar
            </Button>
          </div>
        </Aviso>
      ) : cargando ? (
        <Vacio>Cargando…</Vacio>
      ) : lista.length === 0 ? (
        <Vacio>No hay guardias registrados. Se crean desde la administración de usuarios.</Vacio>
      ) : (
        <div style={est.rejilla}>
          {lista.map((g) => (
            <div
              key={g._id}
              style={{
                ...est.tarjeta,
                borderLeft: `4px solid ${g.onDuty ? "var(--success)" : "var(--border)"}`,
                opacity: g.isActive === false ? 0.6 : 1,
              }}
            >
              <h3 style={est.tarjetaTitulo}>{g.name}</h3>
              <div style={est.tarjetaDato}>
                {g.onDuty ? "De turno ahora" : "Fuera de turno"}
                {g.isActive === false ? " · Inactivo" : ""}
              </div>
              {g.phone && (
                <div style={est.tarjetaDato}>
                  <a href={`tel:${g.phone}`} style={{ color: "var(--gold)" }}>
                    {g.phone}
                  </a>
                </div>
              )}
              <div style={est.tarjetaDato}>
                {g.defaultAccessPoint?.name
                  ? `Punto: ${g.defaultAccessPoint.name}`
                  : "Sin punto de acceso asignado"}
              </div>
              <div style={est.tarjetaAcciones}>
                <Button size="sm" variant="outline" onClick={() => abrir(g)}>
                  Editar ficha
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Ventana
        titulo={editando ? `Ficha de ${editando.name}` : ""}
        abierta={Boolean(editando)}
        alCerrar={() => setEditando(null)}
        pie={
          <>
            <Button variant="ghost" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={guardar} loading={guardando}>
              Guardar
            </Button>
          </>
        }
      >
        {error && <Aviso>{error}</Aviso>}
        <Entrada
          etiqueta="Teléfono"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="7777 7777"
        />
        <Entrada
          etiqueta="Documento"
          value={form.documentId}
          onChange={(e) => setForm({ ...form, documentId: e.target.value })}
        />
        <Seleccion
          etiqueta="Punto de acceso asignado"
          value={form.defaultAccessPoint}
          onChange={(e) => setForm({ ...form, defaultAccessPoint: e.target.value })}
          opciones={[
            { valor: "", etiqueta: "Sin asignar" },
            ...(puntos?.accessPoints || []).map((p) => ({ valor: p._id, etiqueta: p.name })),
          ]}
        />

        {/* Sin el interruptor encendido no se enseña ni el campo: ofrecer
            subir una foto que el servidor va a rechazar es peor que no
            ofrecerla. */}
        {fotosDeGuardia ? (
          <div style={est.campo}>
            <span style={est.etiqueta}>Fotografía</span>
            <input
              type="file"
              accept="image/*"
              style={est.control}
              disabled={subiendo}
              onChange={(e) => subirFoto(e.target.files?.[0])}
            />
          </div>
        ) : (
          <div style={{ fontSize: "var(--text-sm)", color: "var(--text-tertiary)" }}>
            Las fotografías de guardias están desactivadas en esta residencial. Se activan en Configuración.
          </div>
        )}
      </Ventana>
    </div>
  );
}
